import { formatCents, parseMoneyInput } from '../../api/parseMoney'
import { localToday, type LocalDate } from './recurringRules'
import { FORM_COPY, validate, valuesOf, type FormMode, type FormValues } from './useSaveRecurring'

/**
 * The add/edit form's own state, and every rule that is not rendering: the
 * form is a `useReducer` over this, and the screen only draws it.
 *
 * The amount stays TEXT until submit — a half-typed `12.` is not a number yet,
 * and turning it into one per keystroke would rewrite what the user is typing.
 */
export type Draft = Omit<FormValues, 'amountCents'> & { amountText: string }

/** One message per field the form can mark. `amount` is the text field's, not `amountCents`'. */
export type DraftErrors = Partial<Record<'name' | 'amount' | 'nextDue' | 'payingSince', string>>

/** The order focus follows on a failed submit: the fields' order on screen. */
export const ERROR_ORDER = ['name', 'amount', 'nextDue', 'payingSince'] as const

/**
 * Where a form starts. An edit starts from the bill (`valuesOf`); a create
 * from today, Monthly, no account, Apply to past off. A bill made from a
 * transaction starts from what the transaction says: its merchant, the
 * magnitude of its amount (an expense is stored negative), its account, and its
 * date as the start date. Monthly is the one decision left to the user.
 */
export function draftOf(mode: FormMode, today: LocalDate = localToday()): Draft {
  if (mode.kind === 'edit') {
    const { amountCents, ...rest } = valuesOf(mode.bill)
    return { ...rest, amountText: amountCents === null ? '' : formatCents(amountCents) }
  }
  if (mode.kind === 'fromTransaction') {
    const { txn } = mode
    return {
      name: txn.merchant,
      amountText: formatCents(Math.abs(txn.amountCents)),
      frequency: 'monthly',
      nextDue: txn.date,
      accountId: txn.accountId,
      icon: null,
      payingSince: txn.date,
      applyToPast: false,
    }
  }
  return {
    name: '',
    amountText: '',
    frequency: 'monthly',
    nextDue: today,
    accountId: null,
    icon: null,
    payingSince: today,
    applyToPast: false,
  }
}

/** Dirty is "any value differs from where the form started", compared by value. */
export function sameDraft(a: Draft, b: Draft): boolean {
  return (Object.keys(a) as (keyof Draft)[]).every((k) => a[k] === b[k])
}

export type DraftAction = { type: 'change'; patch: Partial<Draft> }

/**
 * Apply one edit. The one cross-field rule lives here: **clearing the account
 * turns Apply to past off** — with no account there is nothing to match past
 * payments against, and a box left ticked under a disabled control would send
 * nothing while claiming otherwise.
 */
export function draftReducer(draft: Draft, action: DraftAction): Draft {
  const next = { ...draft, ...action.patch }
  if (next.accountId === null) next.applyToPast = false
  return next
}

/**
 * Read the draft the way a submit does. `values` is what `useSaveRecurring`
 * sends; `errors` is empty when it may be sent.
 *
 * The amount goes through `parseMoneyInput` and nothing else (rule 4): blank
 * is "required", unreadable is its own sentence and never a truncated number.
 * An edit has no Paying since, so it can never be marked.
 */
export function validateDraft(draft: Draft, kind: FormMode['kind']): { values: FormValues; errors: DraftErrors } {
  const { amountText, ...rest } = draft
  let amountCents: number | null = null
  let unreadable = false
  if (amountText.trim() !== '') {
    try {
      amountCents = parseMoneyInput(amountText)
    } catch {
      unreadable = true
    }
  }
  const values: FormValues = { ...rest, amountCents }
  const found = validate(values)
  const errors: DraftErrors = {}
  if (found.name) errors.name = found.name
  if (kind === 'fromTransaction') {
    // The amount, account and date come from the transaction and cannot be
    // edited, so only what the user typed can be wrong: the start date.
    if (draft.payingSince === null || draft.payingSince === '') errors.payingSince = FORM_COPY.payingSinceRequired
    return { values, errors }
  }
  const amount = unreadable ? FORM_COPY.amountUnreadable : found.amountCents
  if (amount) errors.amount = amount
  if (found.nextDue) errors.nextDue = found.nextDue
  if (kind === 'create' && found.payingSince) errors.payingSince = found.payingSince
  return { values, errors }
}
